'use client'

import { useEffect, useState } from 'react'
import { Download as DownloadIcon, Apple, Monitor, Smartphone, Terminal } from 'lucide-react'
import { Button } from '@/components/ui/button'

type PlatformKey = 'android' | 'windows' | 'macos' | 'linux'

type DownloadLink = {
  version: string
  build?: number
  href: string
  alternatives?: DownloadLink[]
}

type LatestDownloads = Partial<Record<PlatformKey, DownloadLink>>

type DownloadSummary = {
  downloads?: Record<string, number>
  totals?: Record<string, number>
  kinds?: Record<string, string>
  latest?: LatestDownloads | null
}

const platforms = [
  {
    key: 'android' as const,
    name: 'Android beta',
    icon: Smartphone,
    detail: 'Android 8+ · Bluetooth and USB OTG',
    cta: 'Download beta APK',
    note: 'Android may ask you to allow installs from your browser. NewChirp can then check for future beta updates from inside the app.',
  },
  {
    key: 'windows' as const,
    name: 'Windows',
    icon: Monitor,
    detail: 'Windows 10 & 11 · 64-bit installer',
    cta: 'Download .exe',
    note: "If Windows warns that the installer is unrecognized or unsigned, click “More info” → “Run anyway.” We're waiting on our code-signing certificate to be approved, so the build is legitimately unsigned for now.",
  },
  {
    key: 'macos' as const,
    name: 'macOS',
    icon: Apple,
    detail: 'Universal build · Intel & Apple Silicon',
    cta: 'Download .dmg',
  },
  {
    key: 'linux' as const,
    name: 'Linux',
    icon: Terminal,
    detail: 'Debian/Ubuntu · Fedora/RHEL · AppImage',
    cta: 'Download .deb',
  },
]

export function Download() {
  const [counts, setCounts] = useState<Record<string, number>>({})
  const [totals, setTotals] = useState<Record<string, number>>({})
  const [kinds, setKinds] = useState<Record<string, string>>({})
  const [latest, setLatest] = useState<LatestDownloads>({})

  useEffect(() => {
    let cancelled = false
    fetch('/api/downloads', { cache: 'no-store' })
      .then((res) => (res.ok ? res.json() : null))
      .then((data: DownloadSummary | null) => {
        if (cancelled || !data) return
        setCounts(data.downloads ?? {})
        setTotals(data.totals ?? {})
        setKinds(data.kinds ?? {})
        setLatest(data.latest ?? {})
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  // Prefer the per-kind total so renaming an artifact for a new release never
  // resets the number shown here; fall back to the exact path count.
  const countLabel = (href: string) => {
    const kind = kinds[href]
    const value = kind && typeof totals[kind] === 'number' ? totals[kind] : counts[href]
    return typeof value === 'number' ? `${value.toLocaleString()} downloads` : null
  }

  return (
    <section id="download" className="border-b border-border/60 py-12 md:py-16">
      <div className="mx-auto max-w-7xl px-4">
        <div className="mx-auto max-w-2xl text-center">
          <span className="font-mono text-sm text-primary">// download</span>
          <h2 className="mt-3 text-balance text-3xl font-bold tracking-tight sm:text-4xl">
            Get the latest build
          </h2>
          <p className="mt-4 text-pretty leading-relaxed text-muted-foreground">
            NewChirp is distributed as a rolling &quot;next&quot; release, updated frequently with new
            drivers and fixes. Pick your platform to grab the current build — completely free.
          </p>
        </div>

        <div className="mt-12 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {platforms.map((platform) => {
            const Icon = platform.icon
            const download = latest[platform.key]
            return (
              <div
                key={platform.name}
                className="flex h-full flex-col items-center rounded-xl border border-border bg-card p-8 text-center transition-colors hover:border-primary/40"
              >
                <span className="flex h-14 w-14 items-center justify-center rounded-xl border border-border bg-background text-primary">
                  <Icon className="h-7 w-7" />
                </span>
                <h3 className="mt-5 flex flex-wrap items-center justify-center gap-2 text-xl font-semibold">
                  <span>{platform.name}</span>
                  {download?.version && (
                    <span className="rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 font-mono text-xs text-primary">
                      v{download.version}
                    </span>
                  )}
                </h3>
                <p className="mt-1 text-sm text-muted-foreground">{platform.detail}</p>
                <div className="mt-6 flex w-full flex-col gap-2">
                  <Button
                    size="lg"
                    className="w-full"
                    disabled={!download}
                    nativeButton={false}
                    render={<a href={download?.href ?? '/#download'} />}
                  >
                    <DownloadIcon className="h-4 w-4" />
                    {platform.cta}
                  </Button>
                  <span className="min-h-4 font-mono text-xs tabular-nums text-muted-foreground">
                    {download?.href ? (countLabel(download.href) ?? '') : ''}
                  </span>
                  {download?.alternatives?.map((alt) => (
                    <Button
                      key={alt.href}
                      variant="outline"
                      size="lg"
                      className="h-auto w-full justify-start whitespace-normal px-4 py-2.5 text-left"
                      nativeButton={false}
                      render={<a href={alt.href} />}
                    >
                      <span className="flex min-w-0 items-start gap-2">
                        <DownloadIcon className="mt-0.5 h-4 w-4 shrink-0" />
                        <span className="flex min-w-0 flex-col items-start gap-0.5">
                          <span>{alt.href.endsWith('.rpm') ? 'Download .rpm' : 'Download AppImage'}</span>
                          {countLabel(alt.href) && (
                            <span className="font-mono text-[11px] font-normal tabular-nums text-muted-foreground">
                              {countLabel(alt.href)}
                            </span>
                          )}
                        </span>
                      </span>
                    </Button>
                  ))}
                </div>
                {platform.note && (
                  <p className="mt-auto pt-3 text-xs leading-relaxed text-muted-foreground">
                    {platform.note}
                  </p>
                )}
              </div>
            )
          })}

          <div className="flex h-full flex-col items-center rounded-xl border border-border bg-card p-8 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-xl border border-border bg-background text-primary">
              <Apple className="h-7 w-7" />
            </span>
            <h3 className="mt-5 flex flex-wrap items-center justify-center gap-2 text-xl font-semibold">
              <span>iOS</span>
              <span className="rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 font-mono text-xs text-primary">
                Coming very soon
              </span>
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              iPhone &amp; iPad · App Store release in development
            </p>
            <div className="mt-6 flex w-full flex-col gap-2">
              <span className="flex min-h-9 w-full items-center justify-center rounded-lg border border-border bg-secondary/60 px-3 text-sm font-medium text-muted-foreground">
                Coming very soon
              </span>
              <span className="min-h-4 font-mono text-xs text-muted-foreground">
                App Store
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
